import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Clock, Wrench, Monitor, TestTube, ClipboardCheck, CheckCircle2,
  ChevronRight, ChevronDown, ChevronUp, Eye
} from 'lucide-react';
import { useOrderComponents } from '@/hooks/useOrderComponents';
import { AssemblyChecklist, CHECKLIST_SECTIONS, normaliseProgress } from '@/components/AssemblyChecklist';
import { PrintBuildSheet } from '@/components/PrintBuildSheet';
import { PrintZebraLabels } from '@/components/PrintZebraLabels';
import { PrintBuildCertificate } from '@/components/PrintBuildCertificate';
import { AONotes } from '@/components/AONotes';
import { SignaturePad } from '@/components/SignaturePad';
import { StressTestUpload } from '@/components/StressTestUpload';
import { HelpTooltip } from '@/components/HelpTooltip';
import { OpenPauses, PauseButton, PausedBadge } from '@/components/BuildPauses';
import { useOpenPauses } from '@/lib/api/pauses';
import { useMyTeam } from '@/lib/api/team';
import { techLabel } from '@/lib/teamwork';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';
import { formatDate } from '@/lib/utils';
import { DemoSetupBanner } from '@/lib/demoSetup';
import { postToSharePoint } from '@/lib/sharepoint';
import { writeAuditLog } from '@/lib/audit';
import { notifyOrderSubmittedForReview } from '@/lib/notifications';
import { toast } from 'sonner';

type BuildOrderStatus = 'pending' | 'building' | 'software' | 'testing' | 'awaiting_review' | 'completed';

import { PIPELINE_STATUSES } from '@/lib/assemblyOrderStatus';
import { useCurrentUser } from '@/lib/useCurrentUser';
const STATUS_FLOW: BuildOrderStatus[] = [...PIPELINE_STATUSES, 'completed'];

const STATUS_CONFIG: Record<BuildOrderStatus, {
  label: string;
  color: string;
  bg: string;
  icon: any;
  action: string;
  actionColor: string;
  description: string;
}> = {
  pending: {
    label: 'Pending',
    color: 'text-gray-700',
    bg: 'bg-gray-100',
    icon: Clock,
    action: 'Start Building',
    actionColor: 'bg-blue-600 hover:bg-blue-700 text-white',
    description: 'This order is ready to start. Click below when you begin assembly.',
  },
  building: {
    label: 'Building',
    color: 'text-blue-700',
    bg: 'bg-blue-100',
    icon: Wrench,
    action: 'Start Software Installation',
    actionColor: 'bg-indigo-600 hover:bg-indigo-700 text-white',
    description: 'Hardware assembly in progress. Click below when hardware is complete and ready for software.',
  },
  software: {
    label: 'Software Installation',
    color: 'text-indigo-700',
    bg: 'bg-indigo-100',
    icon: Monitor,
    action: 'Start Testing',
    actionColor: 'bg-purple-600 hover:bg-purple-700 text-white',
    description: 'Installing OS and software. Click below when installation is complete and ready to test.',
  },
  testing: {
    label: 'Testing',
    color: 'text-purple-700',
    bg: 'bg-purple-100',
    icon: TestTube,
    action: 'Submit for Review',
    actionColor: 'bg-yellow-500 hover:bg-yellow-600 text-white',
    description: 'Running diagnostics and burn-in tests. Click below when all tests pass.',
  },
  awaiting_review: {
    label: 'Awaiting Review',
    color: 'text-yellow-700',
    bg: 'bg-yellow-100',
    icon: ClipboardCheck,
    action: '',
    actionColor: '',
    description: 'Submitted for admin review. Waiting for approval.',
  },
  completed: {
    label: 'Completed',
    color: 'text-green-700',
    bg: 'bg-green-100',
    icon: CheckCircle2,
    action: '',
    actionColor: '',
    description: 'Build complete and approved.',
  },
};

const WorkflowPipeline = ({ currentStatus }: { currentStatus: BuildOrderStatus }) => {
  const steps = STATUS_FLOW.filter(s => s !== 'completed');
  const currentIdx = STATUS_FLOW.indexOf(currentStatus);
  return (
    <div className="flex items-center gap-1">
      {steps.map((step, idx) => {
        const config = STATUS_CONFIG[step];
        const Icon = config.icon;
        const isActive = step === currentStatus;
        const isDone = STATUS_FLOW.indexOf(step) < currentIdx;
        return (
          <div key={step} className="flex items-center flex-1">
            <div className="flex flex-col items-center gap-1 flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all
                ${isActive ? `${config.bg} ${config.color} border-current shadow ring-2 ring-offset-1 ring-current`
                  : isDone ? 'bg-green-100 text-green-600 border-green-300'
                  : 'bg-muted text-muted-foreground border-border opacity-30'}`}>
                {isDone ? <CheckCircle2 className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
              </div>
              <span className={`text-[10px] font-medium text-center leading-tight
                ${isActive ? config.color : isDone ? 'text-green-600' : 'text-muted-foreground opacity-40'}`}>
                {step === 'software' ? 'Software' : step === 'awaiting_review' ? 'Review' : config.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`h-0.5 flex-1 mx-1 rounded mb-4 ${isDone || isActive ? (isDone ? 'bg-green-300' : 'bg-border') : 'bg-border opacity-30'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
};

/** Auto-fills checklist items for the section that triggers the given status, if not already complete. */
/**
 * Auto-fill checklist items for all sections that precede the target status.
 * e.g. advancing to 'testing' fills Assembly + Software;
 *      advancing to 'awaiting_review' fills Assembly + Software + Benchmarking + Finishing + Final Touches.
 */
/**
 * Auto-fill/rollback checklist items for all units across all sections
 * that precede or follow the target status.
 * Handles both old flat format and new multi-unit format.
 */
function buildAutoProgress(order: any, nextStatus: BuildOrderStatus): Record<string, any> | undefined {
  const STATUS_SECTION_MAP: Record<string, string[]> = {
    software: ['assembly'],
    testing: ['assembly', 'software'],
    awaiting_review: ['assembly', 'software', 'benchmarking', 'finishing', 'final_touches'],
    completed: ['assembly', 'software', 'benchmarking', 'finishing', 'final_touches'],
  };
  const sectionKeys = STATUS_SECTION_MAP[nextStatus] ?? [];
  const unitCount = Math.max(order.quantity || 1, 1);
  const raw = order.checklist_progress;
  const allUnits = normaliseProgress(raw, unitCount);
  let changed = false;

  for (const unitKey of Object.keys(allUnits)) {
    const unitProg = { ...allUnits[unitKey] };
    for (const section of CHECKLIST_SECTIONS) {
      if (sectionKeys.includes(section.key)) {
        for (const item of section.items) {
          if (!unitProg[item.key]) { unitProg[item.key] = true; changed = true; }
        }
      } else {
        for (const item of section.items) {
          if (unitProg[item.key]) { unitProg[item.key] = false; changed = true; }
        }
      }
    }
    allUnits[unitKey] = unitProg;
  }

  return changed ? allUnits : undefined;
}

export function TechnicianOrdersPage() {
  const [detailsOrder, setDetailsOrder] = useState<any>(null);
  const [checklistOrder, setChecklistOrder] = useState<any>(null);
  const [signoffOrder, setSignoffOrder] = useState<any>(null);

  // Per-unit signoff data: serial numbers and benchmark scores keyed by unit index (0-based).
  // GPU driver and OS build ride along with the scores — same table row per unit.
  type BenchScores = { passmark_rating: string; cpu_mark: string; graphics_2d_mark: string; graphics_3d_mark: string; memory_mark: string; disk_mark: string; gpu_driver_version: string; os_build: string };
  const emptyBench = (): BenchScores => ({ passmark_rating: '', cpu_mark: '', graphics_2d_mark: '', graphics_3d_mark: '', memory_mark: '', disk_mark: '', gpu_driver_version: '', os_build: '' });
  const [unitSerials, setUnitSerials] = useState<string[]>(['']);
  const [unitBenchmarks, setUnitBenchmarks] = useState<BenchScores[]>([emptyBench()]);
  const [activeUnit, setActiveUnit] = useState(0);
  // Locally-captured signature URL after signing — held in state so the tech can
  // print the certificate before the form is submitted.
  const [localSignatureUrl, setLocalSignatureUrl] = useState<string | null>(null);

  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [seenOrderIds, setSeenOrderIds] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();

  const { data: currentUser } = useCurrentUser();
  const { data: openPauses = [] } = useOpenPauses();

  // Support both cached shapes (App.tsx uses technicianId, this page uses id)
  const technicianId = currentUser?.id || currentUser?.technicianId;

  // Load seen order IDs from localStorage (keyed per technician)
  useEffect(() => {
    if (!technicianId) return;
    const stored = localStorage.getItem(`seen_orders_${technicianId}`);
    setSeenOrderIds(new Set(stored ? JSON.parse(stored) : []));
  }, [technicianId]);

  const { data: orders, isLoading } = useQuery({
    queryKey: ['technician-orders', technicianId],
    queryFn: async () => {
      if (!technicianId) return [];

      // First try by technician_id
      const { data, error } = await supabase
        .from('assembly_orders')
        .select(`
          *,
          build:builds(id, sku, description, comments),
          reservation:reservations(id, reservation_code),
          technician:technicians!technician_id(name, initials)
        `)
        .eq('technician_id', technicianId)
        .in('status', PIPELINE_STATUSES)
        .order('created_at', { ascending: false });
      if (error) throw error;

      // If no results, the technician_id in assembly_orders may point to a different row
      // (e.g. user linked to a different technician record than the one on the orders)
      // Fall back: find all technician IDs with same name/alias, then query those
      if (data && data.length === 0 && currentUser?.name) {
        const { data: techRows } = await supabase
          .from('technicians')
          .select('id')
          .or(`name.eq.${currentUser.name},alias.eq.${currentUser.alias || currentUser.name}`);

        if (techRows && techRows.length > 0) {
          const ids = techRows.map((t: any) => t.id);
          const { data: fallbackData, error: fallbackError } = await supabase
            .from('assembly_orders')
            .select(`
              *,
              build:builds(id, sku, description, comments),
              reservation:reservations(id, reservation_code),
              technician:technicians!technician_id(name, initials)
            `)
            .in('technician_id', ids)
            .in('status', PIPELINE_STATUSES)
            .order('created_at', { ascending: false });
          if (!fallbackError) return fallbackData || [];
        }
      }

      return data || [];
    },
    enabled: !!technicianId,
  });

  // Teammates: technicians who share this technician's manager. They can see
  // and work each other's orders (see 20261105170000_team_orders.sql).
  const { data: team } = useMyTeam();
  const teammates = (team?.members ?? []).filter(m => m.id !== technicianId);
  const teammateIds = teammates.map(m => m.id);
  const teamNames = new Map((team?.members ?? []).map(m => [m.id, m]));
  const [view, setView] = useState<'mine' | 'team'>('mine');
  const showingTeam = view === 'team' && teammateIds.length > 0;

  const { data: teamOrders, isLoading: teamLoading } = useQuery({
    queryKey: ['technician-orders', 'team', teammateIds.join(',')],
    enabled: showingTeam,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('assembly_orders')
        .select(`
          *,
          build:builds(id, sku, description, comments),
          reservation:reservations(id, reservation_code),
          technician:technicians!technician_id(name, initials)
        `)
        .in('technician_id', teammateIds)
        .in('status', PIPELINE_STATUSES)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });
  const shownOrders = showingTeam ? teamOrders : orders;

  const updateStatus = useMutation({
    mutationFn: async ({ id, newStatus, checklistProgress }: {
      id: string;
      newStatus: BuildOrderStatus;
      oldStatus?: BuildOrderStatus;
      orderNumber?: string;
      checklistProgress?: Record<string, boolean>;
    }) => {
      const updates: any = { status: newStatus };
      if (newStatus === 'building') updates.started_at = new Date().toISOString();
      if (newStatus === 'awaiting_review') updates.completed_at = new Date().toISOString();
      if (checklistProgress) updates.checklist_progress = checklistProgress;
      const { data: updated, error } = await supabase
        .from('assembly_orders')
        .update(updates)
        .eq('id', id)
        .select('id, status')
        .single();
      if (error) throw error;
      if (!updated) throw new Error('Update was blocked — check your permissions or contact an admin.');
    },
    onSuccess: (_, { id, newStatus, oldStatus, orderNumber }) => {
      queryClient.invalidateQueries({ queryKey: ['technician-orders'] });
      queryClient.invalidateQueries({ queryKey: ['active-assembly-orders'] });
      queryClient.invalidateQueries({ queryKey: ['assembly-order-status-counts'] });
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      const config = STATUS_CONFIG[newStatus];
      toast.success(`Order moved to: ${config.label}`);

      writeAuditLog({
        action: 'assembly_order_status_change',
        entity_type: 'assembly_order',
        entity_id: id,
        entity_label: orderNumber,
        old_value: oldStatus ? { status: oldStatus } : null,
        new_value: { status: newStatus },
      });
    },
    onError: (err: any) => toast.error(`Failed to update: ${err.message}`),
  });

  const normaliseStatus = (s: string): BuildOrderStatus => {
    if (s === 'In Progress') return 'pending';
    return (s as BuildOrderStatus) || 'pending';
  };

  const getNextStatus = (s: BuildOrderStatus): BuildOrderStatus | null => {
    const idx = STATUS_FLOW.indexOf(s);
    return idx < STATUS_FLOW.length - 1 ? STATUS_FLOW[idx + 1] : null;
  };

  // Initialise per-unit state when signoff dialog opens — pre-fills from any
  // per-unit data already saved early (via "Save Unit Data"), so the tech only
  // has to fill in whichever units weren't already done.
  const openSignoff = async (order: any) => {
    const qty = Math.max(order.quantity || 1, 1);
    const existingSns: Record<string, string> = order.serial_numbers || {};
    setUnitSerials(
      Array.from({ length: qty }, (_, i) => existingSns[String(i + 1)] || (i === 0 ? (order.serial_number || '') : '')),
    );

    const { data: benchRows } = await supabase
      .from('assembly_order_benchmarks')
      .select('unit_number, passmark_rating, cpu_mark, graphics_2d_mark, graphics_3d_mark, memory_mark, disk_mark, gpu_driver_version, os_build')
      .eq('assembly_order_id', order.id);
    const benchByUnit: Record<number, any> = {};
    (benchRows || []).forEach((b: any) => { benchByUnit[b.unit_number] = b; });
    const numOrBlank = (v: number | null | undefined) => (v != null ? String(v) : '');
    setUnitBenchmarks(Array.from({ length: qty }, (_, i) => {
      const b = benchByUnit[i + 1];
      return b ? {
        passmark_rating: numOrBlank(b.passmark_rating),
        cpu_mark: numOrBlank(b.cpu_mark),
        graphics_2d_mark: numOrBlank(b.graphics_2d_mark),
        graphics_3d_mark: numOrBlank(b.graphics_3d_mark),
        memory_mark: numOrBlank(b.memory_mark),
        disk_mark: numOrBlank(b.disk_mark),
        gpu_driver_version: b.gpu_driver_version || '',
        os_build: b.os_build || '',
      } : emptyBench();
    }));

    setActiveUnit(0);
    setLocalSignatureUrl(null);
    setSignoffOrder(order);
  };

  // Persist units' serial numbers + benchmarks, independent of the order's
  // overall pipeline status — lets a tech print a unit's certificate, or send
  // finished units ahead, without waiting for sibling units to finish.
  const persistUnitData = async (order: any, unitIndexes: number[]) => {
    // Re-fetch current serial_numbers so we merge rather than clobber other units.
    const { data: current, error: readErr } = await supabase
      .from('assembly_orders')
      .select('serial_numbers')
      .eq('id', order.id)
      .single();
    if (readErr) throw readErr;
    const merged: Record<string, string> = { ...(current?.serial_numbers || {}) };
    for (const i of unitIndexes) merged[String(i + 1)] = unitSerials[i].trim();
    const { error: snErr } = await supabase.from('assembly_orders').update({
      serial_number: merged['1'] || null,
      serial_numbers: merged,
    }).eq('id', order.id);
    if (snErr) throw snErr;

    for (const i of unitIndexes) {
      const bench = unitBenchmarks[i];
      const { error: benchErr } = await supabase.from('assembly_order_benchmarks').upsert({
        assembly_order_id: order.id,
        unit_number: i + 1,
        technician_id: technicianId || null,
        passmark_rating: parseInt(bench.passmark_rating) || 0,
        cpu_mark: parseInt(bench.cpu_mark) || null,
        graphics_2d_mark: parseInt(bench.graphics_2d_mark) || null,
        graphics_3d_mark: parseInt(bench.graphics_3d_mark) || null,
        memory_mark: parseInt(bench.memory_mark) || null,
        disk_mark: parseInt(bench.disk_mark) || null,
        gpu_driver_version: bench.gpu_driver_version.trim() || null,
        os_build: bench.os_build.trim() || null,
      }, { onConflict: 'assembly_order_id,unit_number' });
      if (benchErr) throw benchErr;
    }
  };

  const saveUnitData = async (order: any, unitIndex: number) => {
    const sn = unitSerials[unitIndex]?.trim();
    const bench = unitBenchmarks[unitIndex];
    if (!sn) { toast.error(`Unit ${unitIndex + 1}: Serial Number is required before saving.`); return; }
    if (!bench?.passmark_rating) { toast.error(`Unit ${unitIndex + 1}: PassMark Rating is required before saving.`); return; }

    const unitKey = String(unitIndex + 1);
    try {
      await persistUnitData(order, [unitIndex]);
    } catch (err: any) {
      toast.error(`Unit ${unitKey}: could not save — ${err.message}`);
      return;
    }

    writeAuditLog({
      action: 'assembly_order_unit_data_saved',
      entity_type: 'assembly_order',
      entity_id: order.id,
      entity_label: order.order_number,
      new_value: { unit: unitKey, serial_number: sn, passmark_rating: bench.passmark_rating },
      notes: `Unit ${unitKey} data saved early by technician (order still ${order.status}).`,
    });

    toast.success(`Unit ${unitKey} data saved.`);
    queryClient.invalidateQueries({ queryKey: ['technician-orders'] });
  };

  const resetSignoff = () => {
    setUnitSerials(['']);
    setUnitBenchmarks([emptyBench()]);
    setActiveUnit(0);
    setLocalSignatureUrl(null);
    setSignoffOrder(null);
  };

  // Submit sign-off: save per-unit benchmarks + serial numbers, update status, post to SharePoint per unit
  const submitSignoff = async (
    order: any,
    serials: string[] = unitSerials,
    benches: BenchScores[] = unitBenchmarks,
  ) => {
    const qty = Math.max(order.quantity || 1, 1);

    // Validate all units
    for (let i = 0; i < qty; i++) {
      if (!serials[i]?.trim()) { toast.error(`Unit ${i + 1}: Serial Number is required`); setActiveUnit(i); return; }
      if (!benches[i]?.passmark_rating) { toast.error(`Unit ${i + 1}: PassMark Rating is required`); setActiveUnit(i); return; }
    }

    const currentStatus = normaliseStatus(order.status);

    // Build serial_numbers jsonb + keep legacy serial_number in sync
    const serialNumbersMap: Record<string, string> = {};
    for (let i = 0; i < qty; i++) serialNumbersMap[String(i + 1)] = serials[i].trim();

    await supabase.from('assembly_orders').update({
      serial_number: serialNumbersMap['1'] || null,
      serial_numbers: serialNumbersMap,
    }).eq('id', order.id);

    writeAuditLog({
      action: 'assembly_order_serial_numbers_updated',
      entity_type: 'assembly_order',
      entity_id: order.id,
      entity_label: order.order_number,
      new_value: serialNumbersMap,
    });

    // Save benchmarks per unit
    for (let i = 0; i < qty; i++) {
      const bs = benches[i];
      const benchmarkPayload = {
        passmark_rating: parseInt(bs.passmark_rating) || 0,
        cpu_mark: parseInt(bs.cpu_mark) || null,
        graphics_2d_mark: parseInt(bs.graphics_2d_mark) || null,
        graphics_3d_mark: parseInt(bs.graphics_3d_mark) || null,
        memory_mark: parseInt(bs.memory_mark) || null,
        disk_mark: parseInt(bs.disk_mark) || null,
        gpu_driver_version: bs.gpu_driver_version.trim() || null,
        os_build: bs.os_build.trim() || null,
      };

      await supabase.from('assembly_order_benchmarks').upsert({
        assembly_order_id: order.id,
        unit_number: i + 1,
        technician_id: technicianId || null,
        ...benchmarkPayload,
      }, { onConflict: 'assembly_order_id,unit_number' });

      if (i === 0) {
        writeAuditLog({
          action: 'assembly_order_benchmarks_recorded',
          entity_type: 'assembly_order',
          entity_id: order.id,
          entity_label: order.order_number,
          new_value: { units: qty, ...benchmarkPayload } as Record<string, unknown>,
        });
      }
    }

    // Audit the technician sign-off
    writeAuditLog({
      action: 'assembly_order_technician_signoff',
      entity_type: 'assembly_order',
      entity_id: order.id,
      entity_label: order.order_number,
      new_value: { technician_id: technicianId, serial_numbers: serialNumbersMap, units: qty },
      notes: `Technician submitted ${qty} unit${qty > 1 ? 's' : ''} for review`,
    });

    // Update status
    updateStatus.mutate({
      id: order.id,
      newStatus: 'awaiting_review',
      oldStatus: currentStatus,
      orderNumber: order.order_number,
      checklistProgress: buildAutoProgress(order, 'awaiting_review'),
    });

    // Post to SharePoint — one entry per unit (fire-and-forget)
    const { data: { user: authUser } } = await supabase.auth.getUser();
    const autoProgress = buildAutoProgress(order, 'awaiting_review');
    const progressData = autoProgress || order.checklist_progress || {};

    for (let i = 0; i < qty; i++) {
      const unitKey = String(i + 1);
      const checklist = progressData[unitKey] || progressData['1'] || progressData;
      const bs = benches[i];

      postToSharePoint({
        pc_sku: order.build?.sku || order.build_sku || '',
        serial_number: serials[i].trim(),
        windows_edition: '',
        windows_version: '',
        os_build: bs.os_build.trim(),
        gpu_model: '',
        gpu_driver_version: bs.gpu_driver_version.trim(),
        passmark_rating: bs.passmark_rating || '',
        cpu_mark: bs.cpu_mark || '',
        graphics_2d_mark: bs.graphics_2d_mark || '',
        graphics_3d_mark: bs.graphics_3d_mark || '',
        memory_mark: bs.memory_mark || '',
        disk_mark: bs.disk_mark || '',
        branding_stickers: !!checklist.branding_stickers,
        qa_seals: !!checklist.warranty_seals,
        sku_sn_stickers: !!checklist.sku_labels,
        aoi_box: !!checklist.pack_seal,
        windows_license_sticker: !!checklist.windows_license,
        date: new Date().toISOString().split('T')[0],
        technician: authUser?.email || '',
        comments: qty > 1 ? `Unit ${unitKey} of ${qty}` : '',
      }).then(ok => {
        if (!ok) toast.warning(`SharePoint record for unit ${unitKey} could not be created — please add manually.`);
      });
    }

    // Notify admins/managers (fire-and-forget) — list all serial numbers
    notifyOrderSubmittedForReview(
      {
        order_number: order.order_number,
        build_sku: order.build?.sku || order.build_sku || '',
        technician_name: currentUser?.name || authUser?.email || '',
        serial_number: Object.values(serialNumbersMap).join(', '),
        to_number: order.to_number || '',
        submitted_at: new Date().toISOString(),
      },
      order.technician_id || technicianId!,
    );

    resetSignoff();
  };

  // Units with both a serial number and a PassMark rating, 0-based.
  const readyUnitIndexes = () =>
    unitSerials
      .map((sn, i) => (sn?.trim() && unitBenchmarks[i]?.passmark_rating ? i : -1))
      .filter(i => i >= 0);

  // Submit only the finished units of a multi-unit order. They move into a new
  // order numbered after this one (ASO-…-1) and are signed off from there, so
  // they can be reviewed and posted while the rest are still in production.
  const submitReadyUnits = async (order: any) => {
    const qty = Math.max(order.quantity || 1, 1);
    const ready = readyUnitIndexes();
    if (!ready.length || ready.length >= qty) return;
    const units = ready.map(i => i + 1);

    let split: { id: string; order_number: string };
    try {
      // The split carries each unit's saved data across, so save it first.
      await persistUnitData(order, ready);
      const { data, error } = await supabase.rpc('split_assembly_order', {
        p_ao_id: order.id,
        p_units: units,
      });
      if (error) throw error;
      split = data as { id: string; order_number: string };
    } catch (err: any) {
      toast.error(`Could not split ${order.order_number}: ${err.message}`);
      return;
    }
    // The split moved and renumbered stress-test rows on both orders.
    queryClient.invalidateQueries({ queryKey: ['stress-tests'] });

    writeAuditLog({
      action: 'assembly_order_split',
      entity_type: 'assembly_order',
      entity_id: order.id,
      entity_label: order.order_number,
      old_value: { quantity: qty },
      new_value: { quantity: qty - ready.length, split_off: split.order_number, units },
      notes: `Unit${units.length > 1 ? 's' : ''} ${units.join(', ')} moved to ${split.order_number} to be posted ahead of the rest.`,
    });

    const { data: child, error: childErr } = await supabase
      .from('assembly_orders')
      .select(`
        *,
        build:builds(id, sku, description, comments),
        reservation:reservations(id, reservation_code),
        technician:technicians!technician_id(name, initials)
      `)
      .eq('id', split.id)
      .single();
    if (childErr || !child) {
      toast.warning(`${split.order_number} was created but not submitted — open it from My Orders and submit it.`);
      queryClient.invalidateQueries({ queryKey: ['technician-orders'] });
      resetSignoff();
      return;
    }

    toast.success(`Unit${units.length > 1 ? 's' : ''} ${units.join(', ')} split off as ${split.order_number}.`);
    await submitSignoff(
      child,
      ready.map(i => unitSerials[i]),
      ready.map(i => unitBenchmarks[i]),
    );
  };

  const toggleExpand = (id: string) => {
    setExpandedOrders(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Auto-expand any order that has been started (not pending)
  useEffect(() => {
    if (!shownOrders?.length) return;
    setExpandedOrders(prev => {
      const next = new Set(prev);
      (shownOrders as any[]).forEach(o => {
        if (o.status !== 'pending' && o.status !== 'In Progress') next.add(o.id);
      });
      return next;
    });
  }, [shownOrders]);

  // Mark all current orders as seen after 5 seconds (gives time to notice NEW badge)
  useEffect(() => {
    if (!shownOrders?.length || !technicianId) return;
    const timer = setTimeout(() => {
      const allIds = (shownOrders as any[]).map(o => o.id);
      setSeenOrderIds(prev => {
        const next = new Set([...prev, ...allIds]);
        localStorage.setItem(`seen_orders_${technicianId}`, JSON.stringify([...next]));
        return next;
      });
    }, 5000);
    return () => clearTimeout(timer);
  }, [shownOrders, technicianId]);

  if (isLoading || (showingTeam && teamLoading)) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );

  const STATUS_SORT: Record<string, number> = {
    building: 1, 'In Progress': 4, software: 2, testing: 3, pending: 4, awaiting_review: 5,
  };
  const activeOrders = (shownOrders || [])
    .filter(o => normaliseStatus(o.status) !== 'awaiting_review')
    .sort((a, b) => {
      const statusDiff = (STATUS_SORT[a.status] ?? 4) - (STATUS_SORT[b.status] ?? 4);
      if (statusDiff !== 0) return statusDiff;
      // Within the same status, most recently started first
      return new Date(b.started_at || b.created_at).getTime() - new Date(a.started_at || a.created_at).getTime();
    });
  const reviewOrders = (shownOrders || []).filter(o => normaliseStatus(o.status) === 'awaiting_review');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">My Assembly Orders</h1>
        <p className="text-muted-foreground">
          {showingTeam
            ? `Your team · ${activeOrders.length} active build${activeOrders.length !== 1 ? 's' : ''}`
            : `${currentUser?.alias || currentUser?.name} · ${activeOrders.length} active build${activeOrders.length !== 1 ? 's' : ''}`}
        </p>
        {teammateIds.length > 0 && (
          <div className="mt-3 inline-flex rounded-md border p-0.5 text-sm">
            {(['mine', 'team'] as const).map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1 rounded ${view === v ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
              >
                {v === 'mine' ? 'Mine' : `Team (${teammates.map(t => t.initials || techLabel(t)).join(', ')})`}
              </button>
            ))}
          </div>
        )}
      </div>

      {shownOrders?.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <CheckCircle2 className="h-12 w-12 mb-3 text-green-400" />
            <p className="text-lg font-medium">All caught up!</p>
            <p className="text-sm">{showingTeam ? 'Your teammates have no active build orders.' : 'No active build orders assigned to you.'}</p>
          </CardContent>
        </Card>
      )}

      {activeOrders.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Active Builds</h2>
          {activeOrders.map((order) => {
            const status = normaliseStatus(order.status);
            const config = STATUS_CONFIG[status];
            const Icon = config.icon;
            const nextStatus = getNextStatus(status);
            const isExpanded = expandedOrders.has(order.id);
            const orderPauses = openPauses.filter(p => p.assembly_order_id === order.id);
            return (
              <Card key={order.id} className="overflow-hidden">
                <div className={`h-1 ${config.bg.replace('bg-', 'bg-').replace('100', '400')}`} />
                <CardContent className="pt-4">
                  {/* Always-visible header — click to expand/collapse */}
                  <div
                    className="flex items-start justify-between gap-4 cursor-pointer select-none"
                    onClick={() => toggleExpand(order.id)}
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-sm font-bold text-primary">{order.order_number}</span>
                        {!seenOrderIds.has(order.id) && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white animate-pulse">
                            NEW
                          </span>
                        )}
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.color}`}>
                          <Icon className="h-3 w-3" />
                          {config.label}
                        </span>
                        <PausedBadge pauses={orderPauses} quantity={order.quantity} className="text-xs" />
                        {order.build_type && order.build_type !== 'Stock' && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 border border-indigo-200">
                            {order.build_type}
                          </span>
                        )}
                        {order.quantity > 1 && (
                          <span className="text-xs text-muted-foreground">× {order.quantity} builds</span>
                        )}
                      </div>
                      <p className="font-medium">{order.build?.sku || '—'}</p>
                      <p className="text-sm text-muted-foreground truncate max-w-md">{order.build?.description}</p>
                      <TeamLine order={order} me={technicianId} names={teamNames} />
                      {order.notes && (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1 max-w-md">
                          {order.notes}
                        </p>
                      )}
                      {order.reservation && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Reservation: <span className="font-mono">{order.reservation.reservation_code}</span>
                        </p>
                      )}
                      {order.serial_number && (
                        <p className="text-xs text-muted-foreground mt-1">
                          S/N: <span className="font-mono">{order.serial_number}</span>
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="text-right text-sm text-muted-foreground">
                        {order.to_number && <p className="font-mono text-xs">TO: {order.to_number}</p>}
                        <p className="text-xs">{formatDate(order.created_at)}</p>
                      </div>
                      {isExpanded
                        ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                    </div>
                  </div>

                  {/* Collapsible content */}
                  {isExpanded && (
                    <>
                      {/* Workflow pipeline */}
                      <div className="mb-4 mt-4">
                        <div className="flex items-center gap-1 mb-1">
                          <span className="text-xs font-medium text-muted-foreground">Status Flow</span>
                          <HelpTooltip tipKey="assemblyOrders.statusFlow" />
                        </div>
                        <WorkflowPipeline currentStatus={status} />
                      </div>

                      {/* Stage description + action */}
                      <div className={`rounded-lg p-3 ${config.bg} flex items-center justify-between gap-4`}>
                        <p className={`text-sm ${config.color}`}>{config.description}</p>
                        <div className="flex items-center gap-2 shrink-0">
                          <PauseButton order={order} pauses={orderPauses} />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setChecklistOrder(order)}
                            className="h-8"
                          >
                            Checklist
                          </Button>
                          {status !== 'awaiting_review' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openSignoff(order)}
                              className="h-8"
                            >
                              Enter Build Data
                            </Button>
                          )}
                          {nextStatus && nextStatus !== 'completed' && config.action && (
                            <Button
                              className={`${config.actionColor}`}
                              onClick={() => {
                                if (nextStatus === 'awaiting_review') {
                                  openSignoff(order);
                                } else {
                                  updateStatus.mutate({
                                    id: order.id,
                                    newStatus: nextStatus,
                                    oldStatus: status,
                                    orderNumber: order.order_number,
                                    checklistProgress: buildAutoProgress(order, nextStatus),
                                  });
                                }
                              }}
                              disabled={updateStatus.isPending}
                            >
                              {config.action}
                              <ChevronRight className="ml-1 h-4 w-4" />
                            </Button>
                          )}
                          {nextStatus === 'completed' && config.action && (
                            <Button
                              className={`${config.actionColor}`}
                              onClick={() => updateStatus.mutate({
                                id: order.id,
                                newStatus: 'awaiting_review',
                                oldStatus: status,
                                orderNumber: order.order_number,
                                checklistProgress: buildAutoProgress(order, 'awaiting_review'),
                              })}
                              disabled={updateStatus.isPending}
                            >
                              {config.action}
                              <ChevronRight className="ml-1 h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>

                      <OpenPauses order={order} pauses={orderPauses} className="mt-3" />

                      {/* Demo PC setup banner */}
                      {order.build_type === 'Demo' && (
                        <div className="mt-3">
                          <DemoSetupBanner compact />
                        </div>
                      )}

                      {/* Notes */}
                      <div className="mt-3 space-y-1">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Notes</p>
                        <AONotes aoId={order.id} currentUserId={technicianId || ''} />
                      </div>

                      {/* View components / Print */}
                      <div className="mt-2 flex justify-between items-center">
                        <PrintBuildSheet order={order} canViewCosts={false} />
                        <Button variant="ghost" size="sm" onClick={() => setDetailsOrder(order)}>
                          <Eye className="h-4 w-4 mr-1" /> View Components
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {reviewOrders.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-yellow-700">Awaiting Admin Review</h2>
          {reviewOrders.map((order) => {
            const config = STATUS_CONFIG['awaiting_review'];
            const Icon = config.icon;
            return (
              <Card key={order.id} className="border-yellow-200">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-bold text-primary">{order.order_number}</span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                          <Icon className="h-3 w-3" /> Awaiting Review
                        </span>
                      </div>
                      <p className="font-medium mt-1">{order.build?.sku || '—'}</p>
                      {order.serial_number && (
                        <p className="text-xs text-muted-foreground mt-1">
                          S/N: <span className="font-mono">{order.serial_number}</span>
                        </p>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setDetailsOrder(order)}>
                      <Eye className="h-4 w-4 mr-1" /> View
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Components Dialog */}
      <ComponentsDialog order={detailsOrder} onClose={() => setDetailsOrder(null)} technicianId={technicianId || ''} />

      {/* Checklist Dialog */}
      <ChecklistDialog order={checklistOrder} onClose={() => setChecklistOrder(null)} />

      {/* Tech Sign-off Dialog */}
      <Dialog open={!!signoffOrder} onOpenChange={() => resetSignoff()}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Sign Off & Submit for Review</DialogTitle>
            <DialogDescription>
              {signoffOrder?.order_number} — {signoffOrder?.build?.sku}
              {(signoffOrder?.quantity || 1) > 1 && ` — ${signoffOrder.quantity} units`}
            </DialogDescription>
          </DialogHeader>

          {signoffOrder && (() => {
            const qty = Math.max(signoffOrder.quantity || 1, 1);
            const currentBench = unitBenchmarks[activeUnit] || emptyBench();
            return (
              <div className="flex-1 overflow-y-auto space-y-4 pr-1">
                {/* Unit tabs (only shown for multi-unit) */}
                {qty > 1 && (
                  <div className="flex border-b">
                    {Array.from({ length: qty }, (_, i) => {
                      const hasSerial = !!unitSerials[i]?.trim();
                      const hasBench = !!unitBenchmarks[i]?.passmark_rating;
                      const isComplete = hasSerial && hasBench;
                      return (
                        <button
                          key={i}
                          onClick={() => setActiveUnit(i)}
                          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                            activeUnit === i
                              ? 'border-primary text-primary'
                              : 'border-transparent text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          Unit {i + 1}
                          {isComplete && <CheckCircle2 className="inline ml-1 h-3 w-3 text-green-600" />}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Serial Number */}
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Serial Number {qty > 1 ? `(Unit ${activeUnit + 1})` : ''} *
                  </label>
                  <input
                    type="text"
                    className="w-full border rounded px-2 py-1.5 text-sm bg-background font-mono"
                    placeholder="Enter serial number"
                    value={unitSerials[activeUnit] || ''}
                    onChange={e => setUnitSerials(prev => { const next = [...prev]; next[activeUnit] = e.target.value; return next; })}
                  />
                  {!unitSerials[activeUnit]?.trim() && (
                    <p className="text-xs text-orange-600">Serial Number is required before submitting.</p>
                  )}
                </div>

                {/* System Info (per unit) */}
                <div className="grid grid-cols-2 gap-3">
                  {([
                    ['gpu_driver_version', 'GPU Driver Version', 'e.g. 32.0.15.7677'],
                    ['os_build', 'Windows OS Build', 'e.g. 22631.5039'],
                  ] as const).map(([key, label, placeholder]) => (
                    <div key={key} className="space-y-1">
                      <label className="text-[11px] text-muted-foreground">{label}</label>
                      <input
                        type="text"
                        className="w-full border rounded px-2 py-1.5 text-sm bg-background"
                        placeholder={placeholder}
                        value={currentBench[key]}
                        onChange={e => setUnitBenchmarks(prev => {
                          const next = [...prev];
                          next[activeUnit] = { ...next[activeUnit], [key]: e.target.value };
                          return next;
                        })}
                      />
                    </div>
                  ))}
                </div>

                {/* Benchmark scores (per unit) */}
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    PassMark Benchmark Scores {qty > 1 ? `(Unit ${activeUnit + 1})` : ''}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {([
                      ['passmark_rating', 'PassMark Rating *'],
                      ['cpu_mark', 'CPU Mark'],
                      ['graphics_2d_mark', '2D Graphics Mark'],
                      ['graphics_3d_mark', '3D Graphics Mark'],
                      ['memory_mark', 'Memory Mark'],
                      ['disk_mark', 'Disk Mark'],
                    ] as const).map(([key, label]) => (
                      <div key={key}>
                        <label className="text-[11px] text-muted-foreground">{label}</label>
                        <input
                          type="number"
                          className="w-full border rounded px-2 py-1.5 text-sm bg-background tabular-nums"
                          placeholder="0"
                          value={currentBench[key]}
                          onChange={e => setUnitBenchmarks(prev => {
                            const next = [...prev];
                            next[activeUnit] = { ...next[activeUnit], [key]: e.target.value };
                            return next;
                          })}
                        />
                      </div>
                    ))}
                  </div>
                  {!currentBench.passmark_rating && (
                    <p className="text-xs text-orange-600">PassMark Rating is required before submitting.</p>
                  )}
                </div>

                {/* Stress test log for this unit. Saves on its own rather than
                    through the sign-off submit, so an upload failure can never
                    block signing an order off. */}
                <StressTestUpload
                  key={`${signoffOrder.id}-${activeUnit}`}
                  assemblyOrderId={signoffOrder.id}
                  orderNumber={signoffOrder.order_number}
                  unitNumber={activeUnit + 1}
                  technicianId={technicianId}
                  createdBy={technicianId}
                />

                {/* Signature */}
                {signoffOrder.tech_signature_url && signoffOrder.tech_signed_by === technicianId ? (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Existing Signature</p>
                    <img
                      src={signoffOrder.tech_signature_url}
                      alt="Tech signature"
                      className="border rounded-md max-h-24 bg-white"
                    />
                    <p className="text-xs text-muted-foreground">Already signed. You can re-sign below or skip.</p>
                  </div>
                ) : null}

                <SignaturePad
                  aoId={signoffOrder.id}
                  type="tech"
                  signedBy={technicianId || ''}
                  onSigned={(url) => setLocalSignatureUrl(url)}
                  onCancel={() => resetSignoff()}
                />
              </div>
            );
          })()}

          <DialogFooter className="flex-shrink-0 pt-2 border-t justify-between">
            {signoffOrder && (() => {
              const allFieldsFilled =
                unitSerials.every(s => s.trim()) &&
                unitBenchmarks.every(b => b.passmark_rating);
              const activeUnitComplete =
                !!unitSerials[activeUnit]?.trim() && !!unitBenchmarks[activeUnit]?.passmark_rating;
              // Some units finished but not all: offer to send those ahead.
              const readyUnits = readyUnitIndexes();
              const canSubmitReady =
                !allFieldsFilled &&
                readyUnits.length > 0 &&
                readyUnits.length < Math.max(signoffOrder.quantity || 1, 1) &&
                normaliseStatus(signoffOrder.status) !== 'awaiting_review';
              const effectiveSignatureUrl =
                localSignatureUrl ||
                (signoffOrder.tech_signed_by === technicianId ? signoffOrder.tech_signature_url : null) ||
                null;
              const hasSignature = !!effectiveSignatureUrl;
              return (
                <>
                  <PrintBuildCertificate
                    order={signoffOrder}
                    unitsToPrint={[String(activeUnit + 1)]}
                    serialNumbers={Object.fromEntries(unitSerials.map((sn, i) => [String(i + 1), sn]))}
                    benchmarks={Object.fromEntries(unitBenchmarks.map((b, i) => [String(i + 1), {
                      passmark_rating: parseInt(b.passmark_rating) || 0,
                      cpu_mark: parseInt(b.cpu_mark) || null,
                      graphics_3d_mark: parseInt(b.graphics_3d_mark) || null,
                      memory_mark: parseInt(b.memory_mark) || null,
                      disk_mark: parseInt(b.disk_mark) || null,
                    }]).filter(([, v]) => (v as any).passmark_rating))}
                    signatureUrl={effectiveSignatureUrl}
                    variant="outline"
                    size="sm"
                    disabled={!activeUnitComplete}
                  />
                  {normaliseStatus(signoffOrder.status) !== 'awaiting_review' && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!activeUnitComplete}
                      onClick={() => saveUnitData(signoffOrder, activeUnit)}
                    >
                      Save Unit {activeUnit + 1} Data
                    </Button>
                  )}
                  {canSubmitReady && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => submitReadyUnits(signoffOrder)}
                      title="Moves the finished units into their own order so they can be reviewed and posted now"
                    >
                      Submit Unit{readyUnits.length > 1 ? 's' : ''} {readyUnits.map(i => i + 1).join(', ')} Now
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!allFieldsFilled}
                    onClick={() => submitSignoff(signoffOrder)}
                  >
                    {hasSignature ? 'Submit' : 'Skip Signature & Submit'}
                  </Button>
                </>
              );
            })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ComponentsDialog({ order, onClose, technicianId }: { order: any; onClose: () => void; technicianId: string }) {
  const queryClient = useQueryClient();
  const { data: components, isLoading } = useOrderComponents(order);
  const [tab, setTab] = useState<'components' | 'checklist' | 'notes'>(
    order?.build_type === 'Demo' ? 'notes' : 'components'
  );

  return (
    <Dialog open={!!order} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {order?.order_number} — {order?.build?.sku || 'Order Details'}
          </DialogTitle>
          <DialogDescription>
            {order?.build?.description || (order?.reservation?.reservation_code ?? '')}
          </DialogDescription>
        </DialogHeader>

        {order && (
          <div className="space-y-4">
            {/* Demo PC setup banner */}
            {order.build_type === 'Demo' && <DemoSetupBanner />}
            {/* Build Comments (from build definition) */}
            {order.build?.comments && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Build Comments</p>
                <p className="text-sm p-3 bg-blue-50 border border-blue-200 rounded-md whitespace-pre-wrap">
                  {order.build.comments}
                </p>
              </div>
            )}

            {/* Tab switcher */}
            <div className="flex border-b">
              <button
                onClick={() => setTab('components')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  tab === 'components'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                Components
              </button>
              <button
                onClick={() => setTab('checklist')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  tab === 'checklist'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                Checklist <HelpTooltip tipKey="checklist.sections" />
              </button>
              <button
                onClick={() => setTab('notes')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  tab === 'notes'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                Notes
              </button>
            </div>

            {/* Components tab */}
            {tab === 'components' && (
              <>
                {isLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                  </div>
                ) : !components?.length ? (
                  <p className="text-center text-muted-foreground py-8">No components found</p>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left p-3 font-medium">Type</th>
                          <th className="text-left p-3 font-medium">Part Number</th>
                          <th className="text-left p-3 font-medium">Description</th>
                          <th className="text-right p-3 font-medium">Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {components.map((c, idx) => (
                          <tr key={idx} className="border-b last:border-0">
                            <td className="p-3 text-xs text-muted-foreground">{c.typeLabel}</td>
                            <td className="p-3 font-mono text-xs">{c.sku}</td>
                            <td className="p-3 text-xs text-muted-foreground">{c.description || '—'}</td>
                            <td className="p-3 text-right">{c.qty}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {/* Checklist tab */}
            {tab === 'checklist' && (
              <AssemblyChecklist order={order} readOnly={false} />
            )}

            {/* Notes tab */}
            {tab === 'notes' && (
              <AONotes aoId={order.id} currentUserId={technicianId} />
            )}
          </div>
        )}

        <DialogFooter>
          {order && (
            <PrintZebraLabels
              orderId={order.id}
              orderNumber={order.order_number}
              sku={order.build?.sku || order.build_sku || ''}
              description={order.build?.description}
              quantity={order.quantity || 1}
              serialNumber={order.serial_number}
              serialNumbers={order.serial_numbers}
              onSerialsUpdated={() => queryClient.invalidateQueries({ queryKey: ['tech-orders'] })}
            />
          )}
          <PrintBuildSheet order={order} components={components ?? []} canViewCosts={false} />
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ChecklistDialog({ order, onClose }: { order: any; onClose: () => void }) {
  return (
    <Dialog open={!!order} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {order?.order_number} — Checklist
          </DialogTitle>
          <DialogDescription>
            {order?.build?.sku}{order?.build?.description ? ` · ${order.build.description}` : ''}
          </DialogDescription>
        </DialogHeader>

        {order && <AssemblyChecklist order={order} readOnly={false} />}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * On a teammate's order, who it is assigned to; on any order more than one
 * person has built, who built what.
 */
function TeamLine({ order, me, names }: {
  order: any;
  me: string | null | undefined;
  names: Map<string, { name: string | null; initials: string | null; alias?: string | null }>;
}) {
  const builders: string[] = order.builder_ids ?? [];
  const othersBuilt = builders.some(id => id !== order.technician_id);
  const assignedElsewhere = !!order.technician_id && order.technician_id !== me;
  if (!assignedElsewhere && !othersBuilt) return null;
  const label = (id: string) => {
    const t = names.get(id);
    return t ? techLabel(t) : 'another technician';
  };
  return (
    <p className="text-xs text-muted-foreground mt-1">
      {assignedElsewhere && <>Assigned to <span className="font-medium text-foreground">{order.technician?.name || label(order.technician_id)}</span></>}
      {assignedElsewhere && othersBuilt && ' · '}
      {othersBuilt && <>Built by {builders.map(label).join(', ')}</>}
    </p>
  );
}
